/**
 * Client for the AI match-sheet import (EPIC-112).
 *
 * Contract: `team_plan.md` "API contract". Multipart goes through
 * `fetchLoggerWithAuth` (same reasoning as `rosterImportApi.ts`: the shared
 * axios client pins a JSON Content-Type that fights a FormData boundary);
 * JSON calls use `apiClient`.
 */
import { apiClient } from "./api";
import { fetchLoggerWithAuth, API_URL } from "./loggerApi";
import { LONG_TIMEOUT_MS } from "./httpTimeouts";

export const MATCH_SHEET_IMPORT_IMAGE_COUNT = 2;
export const MATCH_SHEET_IMPORT_MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);
const ACCEPTED_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp"];

export type SheetFieldStatus =
  | "ok"
  | "unreadable"
  | "disagreement"
  | "invalid"
  | "cross_sheet";

export interface SheetField {
  value: string | null;
  status: SheetFieldStatus;
  /** Only present for `disagreement`. */
  readings?: (string | null)[];
  normalized?: string | number | null;
}

export interface SheetKitField extends SheetField {
  hex: string | null;
}

export type MatchStatus = "exact" | "candidates" | "none";

export interface PlayerCandidate {
  player_id: string;
  name: string;
  jersey_number: number | null;
  on_team: boolean;
  score: number;
}

export interface PlayerMatch {
  status: MatchStatus;
  player_id: string | null;
  candidates: PlayerCandidate[];
  jersey_mismatch: boolean;
}

export interface EntityCandidate {
  id: string;
  name: string;
  score: number;
}

export interface EntityMatch {
  status: MatchStatus;
  id: string | null;
  candidates: EntityCandidate[];
}

export interface SheetRow {
  row: number;
  number: SheetField;
  first_name: SheetField;
  surname_1: SheetField;
  surname_2: SheetField;
  position: SheetField;
  flags: SheetField;
  captain: boolean;
  player_match: PlayerMatch;
}

export interface SheetStaff {
  role: SheetField;
  first_name: SheetField;
  surname_1: SheetField;
  surname_2: SheetField;
}

export interface SheetHeader {
  team: SheetField;
  date: SheetField;
  time: SheetField;
  venue: SheetField;
  category: SheetField;
  tournament: SheetField;
  season: SheetField;
  kit: { shirt: SheetKitField; shorts: SheetKitField; socks: SheetKitField };
}

export interface ExtractedSheet {
  index: 0 | 1;
  warnings: string[];
  header: SheetHeader;
  starters: SheetRow[];
  substitutes: SheetRow[];
  staff: SheetStaff[];
  delegate: SheetField;
  team_match: EntityMatch;
}

export interface CrossSheetConflict {
  field: "date" | "time" | "venue";
  values: (string | null)[];
}

export interface SheetImportExtraction {
  double_read: boolean;
  sheets: ExtractedSheet[];
  venue_match: EntityMatch;
  competition_match: EntityMatch;
  cross_sheet: CrossSheetConflict[];
  home_suggestion: 0 | 1 | null;
}

export interface SheetImportStatus {
  enabled: boolean;
  double_read: boolean;
}

export interface MatchPlayersRow {
  row: number;
  first_name: string;
  surname_1: string | null;
  surname_2: string | null;
  number: number | null;
}

export class MatchSheetImportApiError extends Error {
  readonly status: number;
  readonly code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "MatchSheetImportApiError";
    this.status = status;
    this.code = code;
  }
}

export type MatchSheetImageError = "type" | "size";

export function validateMatchSheetImage(
  file: File,
): MatchSheetImageError | null {
  const lower = file.name.toLowerCase();
  const typeOk = file.type
    ? ACCEPTED_TYPES.has(file.type)
    : ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
  if (!typeOk) return "type";
  if (file.size > MATCH_SHEET_IMPORT_MAX_IMAGE_BYTES) return "size";
  return null;
}

/** GET /matches/sheet-import/status. Any failure reads as disabled (UX only). */
export async function getSheetImportStatus(): Promise<SheetImportStatus> {
  try {
    const res = await apiClient.get<SheetImportStatus>(
      "/matches/sheet-import/status",
    );
    return {
      enabled: res?.enabled === true,
      double_read: res?.double_read !== false,
    };
  } catch {
    return { enabled: false, double_read: true };
  }
}

async function toApiError(
  response: Response,
): Promise<MatchSheetImportApiError> {
  let detail: unknown;
  try {
    detail = (await response.json())?.detail;
  } catch {
    detail = undefined;
  }
  if (detail && typeof detail === "object") {
    const d = detail as { code?: unknown; message?: unknown };
    return new MatchSheetImportApiError(
      typeof d.message === "string"
        ? d.message
        : response.statusText || `Request failed (${response.status})`,
      response.status,
      typeof d.code === "string" ? d.code : undefined,
    );
  }
  return new MatchSheetImportApiError(
    typeof detail === "string"
      ? detail
      : response.statusText || `Request failed (${response.status})`,
    response.status,
  );
}

/** POST /matches/sheet-import/extract with exactly two images. */
export async function extractMatchSheets(
  images: File[],
): Promise<SheetImportExtraction> {
  const formData = new FormData();
  images.forEach((file) => formData.append("images", file));
  const response = await fetchLoggerWithAuth(
    `${API_URL}/matches/sheet-import/extract`,
    { method: "POST", body: formData },
    LONG_TIMEOUT_MS,
  );
  if (!response.ok) throw await toApiError(response);
  return (await response.json()) as SheetImportExtraction;
}

/** POST /matches/sheet-import/match-players (no AI call). */
export async function matchSheetPlayers(
  teamId: string,
  rows: MatchPlayersRow[],
): Promise<{ row: number; player_match: PlayerMatch }[]> {
  const res = await apiClient.post<{
    rows: { row: number; player_match: PlayerMatch }[];
  }>("/matches/sheet-import/match-players", { team_id: teamId, rows });
  return res?.rows ?? [];
}

export interface QuickCreatedPlayer {
  player_id: string;
  name: string;
  jersey_number: number;
  position: string;
}

/** POST /teams/{id}/players/quick-create (analyst+): provisional player. */
export async function quickCreateProvisionalPlayer(
  teamId: string,
  body: { name: string; jersey_number: number; position: string },
): Promise<QuickCreatedPlayer> {
  return apiClient.post<QuickCreatedPlayer>(
    `/teams/${teamId}/players/quick-create`,
    body,
  );
}

/** POST /teams/{id}/players (admin only): attach an existing player. */
export async function addPlayerToRoster(
  teamId: string,
  body: { player_id: string; jersey_number: number | null; position: string },
): Promise<void> {
  await apiClient.post(`/teams/${teamId}/players`, {
    ...body,
    team_id: teamId,
  });
}
